import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  AsteriskIcon,
  CopyIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { getImageUrl, groupCoverImage } from "simplydo/core/images";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import {
  Checkbox,
  CheckboxWhenChecked,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { H3, H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { EmptyBox } from "components/lib/UI";

import ConfigurableTable from "../ConfigurableTable";
import { useTableSelection } from "../ConfigurableTable/useTableSelection";
import type { AudienceChooserProps } from "./types";

const AudienceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
  }
`;

const AudienceTableContainer = styled.div`
  max-height: 365px;
  overflow-y: auto;
  margin-top: 12px;
  padding-right: 4px;

  overscroll-behavior: contain;
  scrollbar-width: thin;
`;

const AudienceChooser = ({
  visibility,
  organisation,
  challenge,
  onVisibilityUpdated,
}: AudienceChooserProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [audienceType, setAudienceType] = useState<string | null>("");
  const [orgFilter, setOrgFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [firstLoad, setFirstLoad] = useState(true);
  const user = useCurrentUser();
  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const canEditAudience =
    canManageChallenge ||
    util.hasPermission(user, "challenge.editSettings", challenge?._id);

  const audienceQuery = useQuery(
    initUseQuery(api.challenges.getPostable, [challenge._id]),
  );
  const { groups = [], organisations = [] } = audienceQuery.data || {};

  const [submissionCode, setSubmissionCode] = useState(
    challenge.shortcutSubmissions?.challengeCode || "",
  );
  const [editingSubmissionCode, setEditingSubmissionCode] = useState(false);

  const setInitialVisibility = useCallback(() => {
    if (visibility && !util.isEmpty(visibility)) {
      if (
        (visibility.groups && visibility.groups.length > 0) ||
        !(
          visibility.organisations &&
          visibility.organisations.length === 1 &&
          visibility.organisations[0] === organisation._id
        )
      ) {
        setAudienceType("custom");
      } else setAudienceType("organisation");
    } else setAudienceType("organisation");
    if (!visibility) setAudienceType(null);
  }, [visibility, organisation]);

  const updateShortcutSubmission = useCallback(
    (
      data: Record<string, unknown>,
      success?: (updateData: OpenAPI.Schemas["Challenge"]) => void,
      failure: () => void = () => {},
    ) => {
      const currSettings = challenge.shortcutSubmissions || {};
      const update = { ...currSettings, ...data };
      api.challenges.update(
        challenge._id,
        { shortcutSubmissions: update },
        (updateData) => {
          success?.(updateData);
          api.setQueryData(api.challenges.get, [challenge._id], updateData);
        },
        failure,
      );
    },
    [challenge._id, challenge.shortcutSubmissions],
  );

  useEffect(() => {
    if (firstLoad) {
      setInitialVisibility();
      setFirstLoad(false);
    }
  }, [setInitialVisibility, firstLoad]);

  const organisationSelection = useTableSelection<
    (typeof organisations)[number]
  >({
    value: visibility?.organisations ?? [],
    onChange: (organisationIds) =>
      onVisibilityUpdated({ ...visibility, organisations: organisationIds }),
  });
  const groupSelection = useTableSelection<(typeof groups)[number]>({
    value: visibility?.groups ?? [],
    onChange: (groupIds) =>
      onVisibilityUpdated({ ...visibility, groups: groupIds }),
  });

  const togglePublicLink = useCallback(
    (on: boolean) => {
      const updatedVisibility = { ...visibility, publicLink: on };
      onVisibilityUpdated(updatedVisibility);
    },
    [visibility, onVisibilityUpdated],
  );

  const copyLink = useCallback(() => {
    void util.copyToClipboard(
      util.getBaseUrl(
        challenge?.ownerOrganisation,
        `/challenges/${challenge._id}`,
      ),
    );
  }, [challenge]);

  const changeAudienceType = useCallback(
    (type: string) => {
      setAudienceType(type);
      if (type === "organisation")
        onVisibilityUpdated({
          organisations: [organisation._id],
          publicLink: visibility?.publicLink,
        });
    },
    [visibility, organisation, onVisibilityUpdated],
  );

  const filteredOrgs = orgFilter
    ? organisations.filter((o) => o.name.match(new RegExp(orgFilter, "i")))
    : organisations;
  const filteredGroups = groupFilter
    ? groups.filter((g) => g.name.match(new RegExp(groupFilter, "i")))
    : groups;
  const smsDisabled = useMemo(
    () => challenge?.ipManagement?.isEnabled,
    [challenge],
  );

  const { enabledFeatures = [] } = organisation || {};
  const audienceSelected =
    (visibility?.organisations?.length ?? 0) > 0 ||
    (visibility?.groups?.length ?? 0) > 0;
  return (
    <div>
      <Message color="blue">
        {t("challenge.settings.audience.ideaCreators")}
      </Message>
      <Divider hidden />
      <Checkbox
        type="radio"
        name="audienceRadio"
        value="organisation"
        disabled={!canEditAudience}
        checked={audienceType === "organisation"}
        onChange={() => changeAudienceType("organisation")}
      >
        <CheckboxHeader>
          {t("challenge.settings.audience.organisation.title")}{" "}
          <Label size="small">Default</Label>
        </CheckboxHeader>
        <CheckboxContent>
          {t("challenge.settings.audience.organisation.info", {
            orgName: organisation?.name,
          })}
        </CheckboxContent>
      </Checkbox>
      <Checkbox
        type="radio"
        disabled={!canEditAudience}
        name="audienceRadio"
        value="custom"
        checked={audienceType === "custom"}
        onChange={() => changeAudienceType("custom")}
      >
        <CheckboxHeader>
          {t("challenge.settings.audience.custom.title")}
        </CheckboxHeader>
        <CheckboxContent>
          {t("challenge.settings.audience.custom.info")}
        </CheckboxContent>
      </Checkbox>

      {audienceType === "custom" ? (
        audienceQuery?.isLoading ? (
          <Card fluid>
            <CardContent>
              <Loader />
            </CardContent>
          </Card>
        ) : (
          <div>
            <Card fluid>
              <CardContent>
                <AudienceHeader>
                  <H3>
                    {t("challenge.settings.audience.custom.organisations")}
                  </H3>
                  {canEditAudience ? (
                    <Input
                      type="text"

                      size="small"
                      placeholder={t("generic.filter", {
                        type: t("generic.organisations"),
                      })}
                      onChange={(e) => setOrgFilter(e.target.value)}
                      style={{ float: "right" }}
                    />
                  ) : null}
                </AudienceHeader>

                {!filteredOrgs.length ? (
                  <EmptyBox
                    style={{ marginTop: 15 }}
                    title={t(
                      "challenge.settings.audience.custom.organisationsNone",
                    )}
                    message={t(
                      "challenge.settings.audience.custom.organisationsNoneInfo",
                    )}
                  />
                ) : (
                  <AudienceTableContainer>
                    <ConfigurableTable
                      tableKey="orgAudience"
                      data={filteredOrgs}
                      columns={[
                        {
                          key: "name",
                          name: t(
                            "challenge.settings.audience.organisation.title",
                          ),
                          render: ({ item }) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              <Image
                                src={
                                  getImageUrl(
                                    item,
                                    ["logo", "darkLogo"],
                                    ["200x200", "SCALEx200"],
                                  ) ?? undefined
                                }
                                height={20}
                              />
                              {item.name}
                            </div>
                          ),
                        },
                        {
                          key: "userCount",
                          name: t("generic.users"),
                          render: ({ item }) => item.userCount,
                        },
                      ]}
                      selection={
                        canEditAudience ? organisationSelection : undefined
                      }
                    />
                  </AudienceTableContainer>
                )}
              </CardContent>

              {groups && groups.length > 0 && (
                <CardContent>
                  <AudienceHeader>
                    <H3>{t("common:capitalise", { key: "generic.groups" })}</H3>
                    {canEditAudience ? (
                      <Input
                        type="text"

                        size="small"
                        placeholder={t("generic.filter", {
                          type: t("generic.groups"),
                        })}
                        onChange={(e) => setGroupFilter(e.target.value)}
                      />
                    ) : null}
                  </AudienceHeader>

                  {!filteredGroups.length ? (
                    <EmptyBox
                      title={t("challenge.settings.audience.custom.groupsNone")}
                      message={t(
                        "challenge.settings.audience.custom.groupsNoneInfo",
                      )}
                    />
                  ) : (
                    <AudienceTableContainer>
                      <ConfigurableTable
                        tableKey="groupAudience"
                        data={filteredGroups}
                        columns={[
                          {
                            key: "name",
                            name: t("common:capitalise", {
                              key: "generic.group",
                            }),
                            render: ({ item }) => (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                }}
                              >
                                <Image
                                  src={groupCoverImage(item)}
                                  width={35}
                                  height={35}
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: 4,
                                    backgroundColor: theme.secondaryColour,
                                  }}
                                />
                                {item.name}
                              </div>
                            ),
                          },
                          {
                            key: "organisation",
                            name: t(
                              "challenge.settings.audience.organisation.title",
                            ),
                            render: ({ item }) => item.ownerOrganisation?.name,
                          },
                          {
                            key: "userCount",
                            name: t("generic.users"),
                            render: ({ item }) => item.userCount,
                          },
                        ]}
                        selection={canEditAudience ? groupSelection : undefined}
                      />
                    </AudienceTableContainer>
                  )}
                </CardContent>
              )}
            </Card>
          </div>
        )
      ) : null}
      {enabledFeatures?.indexOf("allowPublicChallenges") !== -1 &&
      audienceSelected ? (
        <>
          <Divider section hidden />
          <Checkbox
            toggle
            disabled={
              !canManageChallenge &&
              !util.hasPermission(
                user,
                "challenge.editSettings",
                challenge?._id,
              )
            }
            checked={visibility?.publicLink}
            onChange={(_e, d) => togglePublicLink(d.checked)}
          >
            <CheckboxHeader>
              {t("challenge.settings.audience.publicLink.title")}
            </CheckboxHeader>
            <CheckboxContent>
              {t("challenge.settings.audience.publicLink.enable")}
            </CheckboxContent>
            <CheckboxWhenChecked>
              <Message color="blue">
                Challenges with a public link also become available on your
                organisation&apos;s{" "}
                <a
                  href={util.getBaseUrl(
                    challenge?.ownerOrganisation,
                    "/challenges/public",
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public Challenges Page
                </a>
                .
              </Message>
              <Card fluid>
                <CardContent>
                  {challenge?._id && (
                    <div>
                      <H4>Public link to this {t("generic.challenge")}</H4>
                      <Input
                        size="small"
                        fluid
                        value={util.getBaseUrl(
                          challenge?.ownerOrganisation,
                          `/challenges/${challenge._id}`,
                        )}
                        readOnly
                        action={
                          <Button onClick={copyLink} color="secondary">
                            <Icon icon={CopyIcon} />
                            {t("challenge.settings.audience.copyLink")}
                          </Button>
                        }
                      />
                    </div>
                  )}
                  <Message>
                    {t("challenge.settings.audience.publicLink.info", {
                      appName: util.appName(),
                    })}
                  </Message>
                </CardContent>
              </Card>
            </CheckboxWhenChecked>
          </Checkbox>
        </>
      ) : null}
      <Divider hidden />
      <H4>
        Shortcut submissions
        <Subheader>
          Shortcut submissions offer your users a quick way to submit{" "}
          {t("generic.ideas")} to your {t("generic.challenge")} without having
          to visit the platform.
        </Subheader>
      </H4>

      {smsDisabled && (
        <Message color="orange">
          <Icon icon={TriangleAlertIcon} />
          <MessageHeader>
            Shortcut submissions are disabled for this {t("generic.challenge")}.
            This is because IP management is enabled, you can update this
            setting{" "}
            <Link to={"ip"} style={{ textDecorationLine: "underline" }}>
              here
            </Link>
            .
          </MessageHeader>
        </Message>
      )}
      <Checkbox
        toggle
        disabled={
          smsDisabled ||
          !(
            canManageChallenge ||
            util.hasPermission(user, "challenge.editSettings", challenge?._id)
          )
        }
        checked={challenge.shortcutSubmissions?.enabled}
        onChange={() =>
          updateShortcutSubmission(
            { enabled: !challenge.shortcutSubmissions?.enabled },
            undefined,
            () => {},
          )
        }
      >
        <CheckboxHeader>Enable shortcut submissions</CheckboxHeader>
        <CheckboxContent>
          {`Enabling shortcut submissions allows users to submit ${t("generic.ideas")} using an external process, such as SMS.`}
        </CheckboxContent>
        <CheckboxWhenChecked>
          <Message color="blue" style={{ marginTop: 15 }}>
            <Icon icon={InfoIcon} />
            <MessageHeader>Shortcut submission notice</MessageHeader>
            <MessageContent>
              <p>
                Shortcut submissions will allow users to submit{" "}
                {t("generic.ideas")} without being logged in, circumventing any
                other audience settings for this {t("generic.challenge")}. This
                does not affect the regular audience rules of the submission
                process.
              </p>
            </MessageContent>
          </Message>
          {!visibility?.publicLink ? (
            <Message color="orange" style={{ marginTop: 15 }}>
              <Icon icon={TriangleAlertIcon} />
              <MessageHeader>Potential visibility issue</MessageHeader>
              <MessageContent>
                <p>
                  Once an {t("generic.idea")} is submitted a user will only be
                  able to view their {t("generic.idea")} if they have access to
                  this {t("generic.challenge")} on {util.appName()}. Make sure
                  that users will be able to sign up with a {util.appName()}{" "}
                  account, or alternatively consider enabling{" "}
                  <b>{t("challenge.settings.audience.publicLink.title")}</b>{" "}
                  above to give users access even if they are not logged in. We
                  will automatically supply them with an authenticated link to
                  view their {t("generic.ideas")} once they have submitted.
                </p>
              </MessageContent>
            </Message>
          ) : null}
          <p style={{ marginTop: 15 }}>
            <H5>
              {t("common:capitalise", { key: "generic.challenge" })} submission
              code{" "}
              <Icon
                icon={AsteriskIcon}
                style={{ position: "relative", top: -5, marginLeft: 3 }}
                color="red"
                size="small"
              />
              <Subheader>
                Users will be able to submit to your {t("generic.challenge")}{" "}
                using this identifier. Try to make this code as simple as
                possible so that users can easily interact with your{" "}
                {t("generic.challenge")}. Please note that the following
                keywords are reserved and cannot be used: CONTINUE, CANCEL,
                CHALLENGES
              </Subheader>
            </H5>
          </p>
          <div>
            <Input
              placeholder="LOTR"
              disabled={
                !editingSubmissionCode ||
                smsDisabled ||
                !(
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editSettings",
                    challenge?._id,
                  )
                )
              }
              value={submissionCode}
              onChange={(event) => setSubmissionCode(event.target.value)}
            />
            <Button
              disabled={
                !(
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editSettings",
                    challenge?._id,
                  )
                ) ||
                (editingSubmissionCode &&
                  (submissionCode.length === 0 ||
                    ["CONTINUE", "CANCEL", "CHALLENGES"].includes(
                      submissionCode.trim().toUpperCase(),
                    )))
              }
              style={{ marginLeft: 5 }}
              onClick={() => {
                if (editingSubmissionCode) {
                  updateShortcutSubmission(
                    { challengeCode: submissionCode },
                    () => {
                      toast.success("Activated submission code.");
                      setEditingSubmissionCode(false);
                    },
                    () => {
                      toast.error("Code invalid or already in use.");
                    },
                  );
                } else {
                  setEditingSubmissionCode(true);
                }
              }}
              color="primary"
            >
              {editingSubmissionCode ? "Save" : "Update code"}
            </Button>
          </div>

          <H5>Enabled services</H5>
          <Checkbox
            toggle
            disabled={
              smsDisabled ||
              !(
                canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.editSettings",
                  challenge?._id,
                )
              )
            }
            checked={
              !!challenge.shortcutSubmissions?.externalServices?.sms?.enabled
            }
            onChange={() => {
              const services =
                challenge.shortcutSubmissions?.externalServices || {};
              const newServices = {
                ...services,
                sms: {
                  enabled:
                    !challenge.shortcutSubmissions?.externalServices?.sms
                      ?.enabled,
                },
              };
              updateShortcutSubmission(
                { externalServices: newServices },
                undefined,
                () => {},
              );
            }}
          >
            <CheckboxHeader>SMS</CheckboxHeader>
            <CheckboxContent>
              {`Anyone will be able to text the number ${import.meta.env.VITE_TWILIO_PHONE_NUMBER} with your ${t("generic.challenge")} submission code, and be taken through a SMS-based ${t("generic.idea")} creation process. Following creation of their ${t("generic.idea")}, they will be offered the chance to create an account and assign the new ${t("generic.idea")} to their new account. (Only text based fields can be created by SMS at this time)`}
            </CheckboxContent>
          </Checkbox>
        </CheckboxWhenChecked>
      </Checkbox>
    </div>
  );
};

export default AudienceChooser;
